<template>
  <v-container class="helmi-search-watch-form ma-0 pa-0" fluid>
    <v-row no-gutters>
      <v-col class="fill-height">
        <v-card class="px-4 px-sm-8 px-sm-12" flat>
          <v-toolbar flat>
            <v-toolbar-title class="text-h4 text-md-h3">
              Hakuvahti
            </v-toolbar-title>
          </v-toolbar>

          <v-card-text class="pt-0">
            <v-form ref="searchForm" v-model="valid">
              <v-row no-gutters>
                <v-col v-if="companies.length > 0" class="pt-1 pt-md-3">
                  <helmi-subheader
                    :colors="labelColors || colors"
                    icon="store"
                    label="Toimisto"
                    class="pa-0 mb-1"
                  />
                  <v-select
                    v-model="selectedCompany"
                    :items="companies"
                    item-text="name"
                    return-object
                    placeholder="Valitse toimisto"
                    full-width
                    required
                    :rules="[v => !!v || 'Pakollinen tieto']"
                    dense
                    solo
                  />
                </v-col>

                <helmi-chips
                  id="target_type"
                  v-model="items.target_type"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :options="targetTypes"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="rule"
                  label="Kohde"
                  mandatory
                />

                <helmi-chips
                  id="propertyType"
                  v-model="items.property_types"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :options="propertyTypes"
                  :additional-options="additionalPropertyTypes"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="home_work"
                  label="Kohdetyyppi"
                  label-additional-options="Muut kohdetyypit"
                  multiple
                />
              </v-row>

              <v-row no-gutters>
                <helmi-combo-box
                  id="cities"
                  v-model="items.cities"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="place"
                  label="Kaupunki tai postinumero"
                  multiple
                />
              </v-row>

              <v-row no-gutters>
                <helmi-range-slider
                  id="price"
                  v-model="items.price"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  :max="maxPrice"
                  :step="stepPrice"
                  :step-options="stepOptionsPrice"
                  icon="money"
                  label="Hinta"
                  min="0"
                  suffix="€"
                />

                <helmi-range-slider
                  id="rooms"
                  v-model="items.rooms"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="room_preferences"
                  label="Huoneluku"
                  min="0"
                  max="10"
                  suffix="kpl"
                  step="1"
                />

                <helmi-range-slider
                  id="area"
                  v-model="items.area"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="square_foot"
                  label="Pinta-ala"
                  min="0"
                  max="500"
                  suffix="m2"
                  step="10"
                />
              </v-row>

              <v-row no-gutters>
                <helmi-chips
                  id="requirements"
                  v-model="items.requirements"
                  :container-options="{
                    cols: 12,
                    class: 'py-1 py-md-3',
                  }"
                  :options="requirements"
                  :colors="colors"
                  :label-colors="labelColors"
                  icon="hot_tub"
                  label="Pitää olla"
                  multiple
                />
              </v-row>

              <v-divider class="my-2 my-md-4"/>

              <v-row>
                <helmi-text-field
                  id="name"
                  v-model="customer.name"
                  :container-options="{
                    cols: 12,
                    md: 4,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  :rules="[v => !!v || 'Pakollinen tieto']"
                  icon="person"
                  label="Nimi"
                  dense
                  required
                  solo
                  subheader
                />

                <helmi-text-field
                  id="email"
                  v-model="customer.email"
                  :container-options="{
                    cols: 12,
                    md: 4,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  :rules="[v => !!v || 'Pakollinen tieto']"
                  icon="email"
                  label="Sähköposti"
                  dense
                  required
                  solo
                  subheader
                />

                <helmi-text-field
                  id="phone"
                  v-model="customer.phone"
                  :container-options="{
                    cols: 12,
                    md: 4,
                    class: 'py-1 py-md-3',
                  }"
                  :colors="colors"
                  :label-colors="labelColors"
                  :rules="[v => !!v || 'Pakollinen tieto']"
                  icon="phone"
                  label="Puhelin"
                  dense
                  solo
                  required
                  subheader
                />
              </v-row>

              <v-row no-gutters>
                <v-checkbox
                  id="gdpr"
                  v-model="gdprAgreed"
                  :rules="[v => !!v || 'Pakollinen tieto']"
                  label="Hyväksyn henkilötietojeni sähköisen käsittelyn"
                  hide-details
                  required
                >
                  <template #label>
                    <span class="mr-2" data-test>
                      Hyväksyn henkilötietojeni sähköisen käsittelyn
                    </span>

                    <v-dialog
                      v-model="dialog"
                      persistent
                      max-width="980"
                    >
                      <template #activator="{ on, attrs }">
                        <a
                          href="#"
                          v-bind="attrs"
                          v-on="on"
                          @click.prevent.stop
                        >
                          Lisätietoja
                        </a>
                      </template>
                      <v-card>
                        <v-card-title class="text-h5">
                          GDPR:n mukainen selostusliite
                        </v-card-title>
                        <v-card-text>
                          <p class="mt-4">
                            <b>Rekisterinpitäjä:</b><br>
                            {{ companyName }}
                          </p>
                          <p>
                            <b>Henkilötietojen käsittelijä:</b><br>
                            RMR Holding Oy / Helmi-kiinteistönvälitysjärjestelmä<br>
                            Männistönkatu 7<br>
                            26100 Rauma<br>
                            y-tunnus: 2402115-6
                          </p>
                          <p>
                            <b>Henkilötietojen käsittelyyn liittyvät yhteydenotot:</b><br>
                            tekniikka@rmr.fi
                          </p>
                          <p>
                            <b>Henkilötietojen käsittelyn käyttötarkoitus:</b><br>
                            Keräämme henkilötietoja rekisterinpitäjän (jäljempänä yritys)
                            asiakassuhteen hoitamista varten. Henkilötietojen käsittelyn
                            oikeusperusteena on käyttäjän ja yrityksen välinen sopimus ja siitä
                            johtuvat lakisääteiset velvoitteet. Henkilötietojen antaminen on
                            edellytys sopimuksen syntymiselle. Toisin sanoen et voi täyttää
                            yrityksen sähköisiä lomakkeita antamatta henkilötietojasi (nimi,
                            sosiaaliturvatunnus, puhelinnumero ja yhteystiedot).
                            Emme tee henkilötietoja koskevia profilointeja tai automaattisia
                            päätöksiä.
                          </p>
                          <p>
                            <b>Henkilötietojasi vastaanottavat:</b><br>
                            <ul>
                              <li>sinä itse</li>
                              <li>rekisterinpitäjä</li>
                              <li>henkilötietojen käsittelijä</li>
                              <li>Mandrill-sähköpostinvälityspalvelu</li>
                            </ul>
                            <br>
                            Mikäli päädyt tekemään tarjouksen, henkilötietojasi voivat saada toinen
                            kauppaosapuoli, pankit tai rahoituslaitokset, isännöintitoimistot,
                            huoneistojärjestelmät, osakehuoneistorekisterit, maanmittauslaitos,
                            kaupanvahvistaja, kiinteistötietojärjestelmät,
                            vuokraoikeuden omistajatahot.
                            <br><br>
                            Yritys on velvollinen pyytämään sinulta erikseen suostumuksen mikäli
                            henkilötietojasi välitetään jollekin muulle kuin edellä mainituille
                            osapuolelle. Henkilötietojen käsittelijän
                            tarjoama tietojärjestelmä on suojattu yritystoiminnassa normaalisti
                            käytössä olevilla teknisillä suojausmenetelmillä. Rekisterin
                            käyttöoikeus edellyttää yrityksen työntekijöiltä
                            henkilökohtaista käyttäjätunnusta ja kirjautumista.
                          </p>
                          <p>
                            <b>Henkilötietojesi säilytysaika:</b><br>
                            Rekisterinpitäjä säilyttää tietojasi viiden vuoden ajan viimeisen
                            palvelupyyntösi jälkeen. Mikäli teet uuden palvelupyynnön, alkaa tämä
                            ajan laskenta alusta.
                          </p>
                          <p>
                            <b>Sinulla on seuraavat oikeudet:</b><br>
                            <ul>
                              <li>Oikeus tarkastaa itseäsi koskevat henkilötiedot</li>
                              <li>Oikeus tietojen oikaisemiseen</li>
                              <li>Oikeus käsittelyn rajoittamiseen</li>
                              <li>Oikeus vastustaa käsittelyä</li>
                              <li>Oikeus peruuttaa suostumus</li>
                              <li>Oikeus tehdä valitus valvontaviranomaiselle</li>
                            </ul>
                            <br>
                            Huomioithan, että sinulla on oikeus tulla unohdetuksi vain, jos meillä
                            ei ole lakisääteisiä velvoitteita jatkaa henkilötietojesi käsittelyä.
                          </p>
                        </v-card-text>
                        <v-card-actions>
                          <v-spacer/>
                          <v-btn
                            color="primary"
                            text
                            @click="dialog = false"
                          >
                            Sulje
                          </v-btn>
                        </v-card-actions>
                      </v-card>
                    </v-dialog>
                  </template>
                </v-checkbox>
              </v-row>
            </v-form>
          </v-card-text>

          <v-card-actions>
            <v-spacer/>
            <v-btn
              id="submit"
              color="primary"
              @click="onButtonClick"
            >
              Luo hakuvahti
            </v-btn>
          </v-card-actions>
          <v-card-actions>
            <v-spacer/>
            <div class="caption">
              This site is protected by reCAPTCHA and the Google
              <a href="https://policies.google.com/privacy">Privacy Policy</a> and
              <a href="https://policies.google.com/terms">Terms of Service</a> apply.
            </div>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';
import { EventBus, isAnObject } from '@/utils';
import { apiPost, apiGet } from '@/api';

export default {
  name: 'HelmiSearchWatchForm',

  components: {
    'helmi-chips': () => import('@/components/formElements/Chips'),
    'helmi-combo-box': () => import('@/components/formElements/ComboBox'),
    'helmi-range-slider': () => import('@/components/formElements/RangeSlider'),
    'helmi-text-field': () => import('@/components/formElements/TextField'),
    'helmi-subheader': () => import('@/components/formElements/Subheader'),
  },

  props: {
    items: {
      type: Object,
      default: () => ({}),
    },

    additionalPropertyTypes: {
      type: Array,
      default: () => [],
    },

    requirements: {
      type: Array,
      default: () => [],
    },

    propertyTypes: {
      type: Array,
      default: () => [],
    },

    targetTypes: {
      type: Array,
      default: () => [],
    },

    color: {
      type: String,
      default: 'primary',
    },

    colors: {
      type: Object,
      default: () => ({
        base: 'primary',
        text: 'primary--text',
      }),
    },

    customer: {
      type: Object,
      default: () => ({}),
    },

    labelColors: {
      type: Object,
      default: () => null,
    },
  },

  data: vm => ({
    selectedCompany: null,
    companyName: '',
    isAnObject,
    dialog: false,
    gdprAgreed: false,
    step: 1,
    translations: {
      target_type: {
        label: 'Kohde',
        suffix: '',
        items: vm.getItems(vm.targetTypes),
      },
      property_types: {
        label: 'Kohdetyyppi',
        suffix: '',
        items: vm.getItems(vm.propertyTypes),
      },
      requirements: {
        label: 'Pitää olla',
        suffix: '',
        items: vm.getItems(vm.requirements),
      },
      rooms: {
        label: 'Huoneluku',
        suffix: 'kpl',
      },
      area: {
        label: 'Pinta-ala',
        suffix: 'm2',
      },
      price: {
        label: 'Hinta',
        suffix: '€',
      },
      cities: {
        label: 'Kaupunki',
        suffix: '',
      },
      neighborhoods: {
        label: 'Kaupunginosa',
        suffix: '',
      },
      zips: {
        label: 'Postinumero',
        suffix: '',
      },
    },
    valid: false,
  }),

  computed: {
    ...mapState({
      companies: state => state.companies,
    }),

    companyId() {
      if (this.selectedCompany) {
        return this.selectedCompany.id;
      }
      return this.$route.params.companyId;
    },

    maxPrice() {
      return this.items.target_type === 'rental' ? 5000 : 1000000;
    },

    stepOptionsPrice() {
      return this.items.target_type === 'rental'
        ? { limit: 1000, step: [10, 50] }
        : { limit: 200000, step: [10000, 50000] };
    },

    stepPrice() {
      return this.items.target_type === 'rental' ? 50 : 10000;
    },
  },

  watch: {
    companyId: {
      async handler(id) {
        if (!id) return;

        const response = await apiGet(`properties/getcompanynamebyid/${id}`);
        this.companyName = response.result.name;
      },
      immediate: true,
    },

    companies: {
      handler(companies) {
        if (!companies.length) return;

        this.selectedCompany = this.selectedCompany || companies[0];
      },
      immediate: true,
      deep: true,
    },
  },

  mounted() {
    EventBus.$on('FORM_RESET', this.resetForm);
  },

  methods: {
    getItems(items) {
      return items.reduce((obj, item) => {
        // eslint-disable-next-line no-param-reassign
        obj[item.value] = item.label;
        return obj;
      }, {});
    },

    async onButtonClick() {
      if (!this.validate()) return;

      await this.recaptcha();
    },

    async recaptcha() {
      // skip recaptcha while testing
      if (process.env.NODE_ENV === 'test') {
        this.$emit('save', {
          items: this.items,
          customer: this.customer,
          companyId: this.companyId,
        });

        return;
      }

      // wait until recaptcha has been loaded.
      await this.$recaptchaLoaded();

      // Execute reCAPTCHA with action "submitSearchWatch".
      const token = await this.$recaptcha('submitSearchWatch');

      // Do stuff with the received token.
      const verification = await apiPost('authorization/verify-captcha', { token });

      // valid captcha
      if (
        verification.success
        && verification.action === 'submitSearchWatch'
        && verification.score >= 0.5
      ) {
        this.$emit('save', {
          items: this.items,
          customer: this.customer,
          companyId: this.companyId,
        });
      }
    },

    resetForm() {
      this.$refs.searchForm.reset();
    },

    validate() {
      return this.$refs.searchForm.validate();
    },
  },
};
</script>

<style lang="scss">
.helmi-search-watch-form {
  height: 100%;

  > .row,
  .v-card {
    height: 100%;
  }

  .v-text-field--solo {
    &.error--text > .v-input__control > .v-input__slot {
      box-shadow:
        0 0 0 1px rgba(255, 82, 82, 0.75),
        0px 3px 1px -2px rgba(255, 82, 82, 0.2),
        0px 2px 2px 0px rgba(255, 82, 82, 0.14),
        0px 1px 5px 0px rgba(255, 82, 82, 0.12);
    }
  }
}
</style>
